window.UnsubWishlistSync = window.UnsubWishlistSync || (() => {
  const buttonSelector = '.unsub-swym-wishlist-collection[data-product-id], .unsub-swym-wishlist-collection[class*="product_"]';

  function getProductId(data) {
    const id = Number(data?.empi || data?.productId || data?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function getVariantId(data) {
    const id = Number(data?.epi || data?.variantId || data?.vid);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  function buttonMatches(button, productId, variantId) {
    const buttonProductId = Number(button.dataset.productId);
    const buttonVariantId = Number(button.dataset.variantId);

    if (variantId && buttonVariantId === variantId) return true;
    if (productId && buttonProductId === productId) return true;
    if (productId && button.classList.contains('product_' + productId)) return true;

    return false;
  }

  function setButtonState(button, isAdded) {
    button.classList.toggle('swym-added', isAdded);
    button.dataset.swaction = isAdded ? 'removeFromWishlist' : 'addToWishlist';
    button.setAttribute('aria-label', isAdded ? 'Remove from Wishlist' : 'Add to Wishlist');
  }

  function sync(data, isAdded) {
    const productId = getProductId(data);
    const variantId = getVariantId(data);
    if (!productId && !variantId) return;

    document.querySelectorAll(buttonSelector).forEach((button) => {
      if (buttonMatches(button, productId, variantId)) {
        setButtonState(button, isAdded);
      }
    });
  }

  function bindSwymEvents(swat) {
    if (!swat?.evtLayer || !swat?.JSEvents || swat.__unsubWishlistSyncBound) return;
    swat.__unsubWishlistSyncBound = true;

    swat.evtLayer.addEventListener(swat.JSEvents.addedToWishlist, (event) => {
      sync(event?.detail?.d || {}, true);
    });

    if (swat.JSEvents.removedFromWishlist) {
      swat.evtLayer.addEventListener(swat.JSEvents.removedFromWishlist, (event) => {
        sync(event?.detail?.d || {}, false);
      });
    }
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(bindSwymEvents);
  if (window._swat) bindSwymEvents(window._swat);

  return { sync, setButtonState };
})();
/**
 * ============================================================================
 * 1. QuickViewStore (Centralized Reactive State Management)
 * ============================================================================
 */
class QuickViewStore extends EventTarget {
  constructor() {
    super();
    this.state = {
      isOpen: false,
      activeHandle: null,
      productData: null,
      masterGroupData: null,
      cartData: null,
      selectedOptions: [],
      selectedVariant: null
    };
  }

  updateState(newState) {
    this.state = { ...this.state, ...newState };
    this.dispatchEvent(new CustomEvent('state-changed', { detail: this.state }));
  }

  async openQuickView({ productId, handle }) {
    const resolvedHandle = handle || window.QuickViewProductHandles?.[String(productId)];
    if (!resolvedHandle) {
      console.error('Quick view requires a product handle or a registered product id map.');
      return;
    }

    this.updateState({
      isOpen: true,
      activeHandle: resolvedHandle,
      productData: null,
      masterGroupData: null,
      cartData: null,
      selectedOptions: [],
      selectedVariant: null
    });

    try {
      const [response, cartResponse] = await Promise.all([
        fetch(`/products/${resolvedHandle}?section_id=cart-edit-product-json`),
        fetch('/cart.js')
      ]);
      const htmlText = await response.text();
      const cartData = cartResponse.ok ? await cartResponse.json() : null;
      const doc = new DOMParser().parseFromString(htmlText, 'text/html');
      const jsonScript = doc.querySelector('script[type="application/json"]') || doc.querySelector('.cart-edit-json-payload-wrapper script');
      if (!jsonScript) throw new Error('Quick view product JSON payload missing.');

      const networkPayload = JSON.parse(jsonScript.textContent);
      const masterGroupData = networkPayload.swatch_group_products || {};
      const currentProductData = masterGroupData[resolvedHandle] || networkPayload;
      const currentVariant = currentProductData.variants?.find(variant => variant.available) || currentProductData.variants?.[0];

      this.updateState({
        masterGroupData,
        cartData,
        productData: currentProductData,
        selectedVariant: currentVariant,
        selectedOptions: currentVariant ? currentVariant.options : currentProductData.variants?.[0]?.options || []
      });
    } catch (error) {
      console.error('Quick view fetch error:', error);
    }
  }

  closeModal() {
    this.updateState({
      isOpen: false,
      activeHandle: null,
      productData: null,
      masterGroupData: null,
      cartData: null,
      selectedOptions: [],
      selectedVariant: null
    });
  }

  switchProductLocal(newHandle, fallbackVariantId) {
    const masterMap = this.state.masterGroupData;
    if (!masterMap || !masterMap[newHandle]) return;

    const targetProductData = masterMap[newHandle];
    const fallbackVariant = targetProductData.variants.find(v => String(v.id) === String(fallbackVariantId)) || targetProductData.variants[0];
    const targetOptionsArray = targetProductData.options.map((option, index) => {
      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
      if (isColorOption) {
        return targetProductData.color_name || masterMap[newHandle]?.color_name || fallbackVariant?.options[index] || option.values[0];
      }

      const currentValue = this.state.selectedOptions[index];
      return option.values.includes(currentValue) ? currentValue : fallbackVariant?.options[index] || option.values[0];
    });
    const targetVariant = targetProductData.variants.find(variant =>
      variant.options.every((optionValue, index) => optionValue === targetOptionsArray[index])
    ) || fallbackVariant;

    this.updateState({
      ...this.state,
      activeHandle: newHandle,
      productData: targetProductData,
      selectedVariant: targetVariant,
      selectedOptions: targetVariant ? targetVariant.options : targetOptionsArray
    });
  }

  setSelectedOptions(dropdownSelections) {
    const { productData, activeHandle, masterGroupData } = this.state;
    if (!productData) return;

    const targetOptionsArray = productData.options.map((option, index) => {
      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
      if (isColorOption) {
        const activeColorSwatch = masterGroupData[activeHandle];
        return activeColorSwatch ? activeColorSwatch.color_name : option.values[0];
      }
      return dropdownSelections[index];
    });
    const selectedVariant = productData.variants.find(variant =>
      variant.options.every((optValue, index) => optValue === targetOptionsArray[index])
    );

    this.updateState({
      ...this.state,
      selectedOptions: targetOptionsArray,
      selectedVariant
    });
  }

  getMaxQuantityForState(state = this.state) {
    const variant = state.selectedVariant;
    if (!variant) return 1;

    const inventoryQuantity = Number(variant.inventory_quantity);
    const hasFiniteInventory = variant.inventory_policy === 'deny'
      && !!variant.inventory_management
      && Number.isFinite(inventoryQuantity);

    if (!hasFiniteInventory) return null;

    const reservedQuantity = (state.cartData?.items || []).reduce((sum, item) => {
      if (String(item.variant_id) !== String(variant.id)) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);

    return Math.max(0, inventoryQuantity - reservedQuantity);
  }
}

window.QuickViewStore = window.QuickViewStore || new QuickViewStore();
window.QuickViewProductHandles = window.QuickViewProductHandles || {};

/**
 * ============================================================================
 * 2. Trigger Components & Click Listeners
 * ============================================================================
 */
class QuickViewButton extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound === 'true') return;
    this.dataset.bound = 'true';
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const handle = this.dataset.productHandle || window.QuickViewUtils.getHandleFromElement(this);
      if (handle) window.QuickViewProductHandles[String(this.dataset.productId)] = handle;
      window.QuickViewStore.openQuickView({
        productId: this.dataset.productId,
        handle
      });
    });
  }
}
customElements.define('quick-view-button', QuickViewButton);

window.QuickViewUtils = window.QuickViewUtils || {
  getHandleFromElement(element) {
    const productLink = element.closest('.product-block, .collection-block, [data-product-id]')?.querySelector('a[href*="/products/"]')
      || element.closest('a[href*="/products/"]')
      || element.querySelector?.('a[href*="/products/"]');
    const href = productLink?.getAttribute('href');
    const match = href && href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }
};


async function sortQuickViewSwatchesByLightness(root = document) {
  const swatchesContainers = root.querySelectorAll('.quick-view-swatch-row');

  const getLightness = (r, g, b) => {
    const [rNorm, gNorm, bNorm] = [r, g, b].map(c => c / 255);
    const max = Math.max(rNorm, gNorm, bNorm);
    const min = Math.min(rNorm, gNorm, bNorm);
    return (max + min) / 2;
  };

  const colorCache = {};

  const localColorCache = localStorage.getItem('swatchColorCache');
  if (localColorCache) {
    try {
      Object.assign(colorCache, JSON.parse(localColorCache));
    } catch (e) {
      console.warn('Failed to parse swatch color cache:', e);
    }
  }

  const swatchWorkerBlob = new Blob([`
    self.onmessage = async function (e) {
      const { imageUrl } = e.data;
      try {
        const response = await fetch(imageUrl, { mode: 'cors' });
        if (!response.ok) throw new Error(response.status + ' ' + response.statusText);
        const blob = await response.blob();
        const imgBitmap = await createImageBitmap(blob);

        const canvas = new OffscreenCanvas(imgBitmap.width, imgBitmap.height);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imgBitmap, 0, 0);

        const imageData = ctx.getImageData(0, 0, imgBitmap.width, imgBitmap.height);
        const { data } = imageData;
        let r = 0, g = 0, b = 0;

        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
        }

        const pixelCount = data.length / 4;
        const avgColor = [
          Math.round(r / pixelCount),
          Math.round(g / pixelCount),
          Math.round(b / pixelCount)
        ];

        self.postMessage({ imageUrl, avgColor });
      } catch (err) {
        self.postMessage({ imageUrl, error: err.toString() });
      }
    };
  `], { type: 'application/javascript' });

  const swatchWorkerUrl = URL.createObjectURL(swatchWorkerBlob);
  const worker = new Worker(swatchWorkerUrl);

  const getAverageColor = (imageUrl) => {
    if (colorCache[imageUrl]) {
      return Promise.resolve(colorCache[imageUrl]);
    }

    return new Promise((resolve, reject) => {
      const handleMessage = (e) => {
        if (e.data.imageUrl !== imageUrl) return;
        worker.removeEventListener('message', handleMessage);

        if (e.data.error) {
          reject(e.data.error);
        } else {
          colorCache[imageUrl] = e.data.avgColor;

          try {
            localStorage.setItem('swatchColorCache', JSON.stringify(colorCache));
          } catch (err) {
            console.warn('LocalStorage full or error saving swatchColorCache', err);
          }

          resolve(e.data.avgColor);
        }
      };

      worker.addEventListener('message', handleMessage);
      worker.postMessage({ imageUrl });
    });
  };

  try {
    for (const container of swatchesContainers) {
      const swatches = Array.from(container.querySelectorAll('.quick-view-swatch-ring'));
      const swatchesWithLightness = [];

      for (let i = 0; i < swatches.length; i++) {
        const swatch = swatches[i];
        const button = swatch.querySelector('button');
        if (!button) {
          swatchesWithLightness.push({ swatch, lightness: 1 });
          continue;
        }

        const imageUrlCandidates = [
          button.getAttribute('data-bgset'),
          button.getAttribute('data-fallback-bgset')
        ];
        const backgroundImage = button.style.backgroundImage;
        const match = backgroundImage.match(/url\(["']?(.*?)["']?\)/);
        if (match) imageUrlCandidates.push(match[1]);

        const normalizedImageUrls = [...new Set(imageUrlCandidates
          .filter(Boolean)
          .map((imageUrl) => {
            const absoluteUrl = imageUrl.startsWith('//') ? 'https:' + imageUrl : imageUrl;
            return absoluteUrl.replace('_48x48_', '_4x4_');
          }))];

        if (!normalizedImageUrls.length) {
          swatchesWithLightness.push({ swatch, lightness: 1 });
          continue;
        }

        let sorted = false;
        for (const imageUrl of normalizedImageUrls) {
          try {
            const [r, g, b] = await getAverageColor(imageUrl);
            const lightness = getLightness(r, g, b);
            swatchesWithLightness.push({ swatch, lightness });
            sorted = true;
            break;
          } catch (err) {
            console.warn(err);
          }
        }

        if (!sorted) {
          swatchesWithLightness.push({ swatch, lightness: 1 });
        }

        if (i % 10 === 0) {
          await new Promise(res => setTimeout(res, 0));
        }
      }

      swatchesWithLightness
        .sort((a, b) => b.lightness - a.lightness)
        .forEach(({ swatch }) => container.appendChild(swatch));
    }
  } finally {
    worker.terminate();
    URL.revokeObjectURL(swatchWorkerUrl);
  }
}
document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-quick-view-product-id]');
  if (!trigger) return;

  event.preventDefault();
  const handle = trigger.dataset.quickViewProductHandle || window.QuickViewUtils.getHandleFromElement(trigger);
  if (handle) window.QuickViewProductHandles[String(trigger.dataset.quickViewProductId)] = handle;
  window.QuickViewStore.openQuickView({
    productId: trigger.dataset.quickViewProductId,
    handle
  });
});

/**
 * ============================================================================
 * 3. Shell Shell Orchestrator Component
 * ============================================================================
 */
class QuickViewModal extends HTMLElement {
  connectedCallback() {
    this.innerHTML = `
      <div class="quick-view-backdrop">
        <div class="quick-view-content">
          <button class="quick-view-close" type="button" aria-label="Close quick view">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M0 0L15 15M15 0L0 15" stroke="#111111" stroke-width="1"/>
            </svg>
          </button>
          <div class="quick-view-container-root"></div>
        </div>
      </div>
      <div class="quick-view-size-chart-overlay" id="quickViewSizeChartDrawer">
        <div class="quick-view-size-chart-content">
          <button class="quick-view-size-chart-close" type="button" aria-label="Close size chart">
            <svg fill="#000000" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
              <path d="M0 0h24v24H0z" fill="none"></path>
            </svg>
          </button>
          <div id="quickViewSizeChartTarget"></div>
        </div>
      </div>
    `;

    this.backdrop = this.querySelector('.quick-view-backdrop');
    this.closeBtn = this.querySelector('.quick-view-close');
    this.container = this.querySelector('.quick-view-container-root');
    this.chartDrawer = this.querySelector('#quickViewSizeChartDrawer');

    this.backdrop.addEventListener('click', (event) => {
      if (event.target === this.backdrop || event.target.closest('.quick-view-close')) {
        window.QuickViewStore.closeModal();
      }
    });
    this.closeBtn.addEventListener('click', () => window.QuickViewStore.closeModal());
    this.chartDrawer?.addEventListener('click', (event) => {
      if (event.target === this.chartDrawer || event.target.closest('.quick-view-size-chart-close')) {
        this.chartDrawer.style.display = 'none';
      }
    });

    window.QuickViewStore.addEventListener('state-changed', (event) => this.handleStateChange(event.detail));
  }

  handleStateChange(state) {
    if (state.isOpen) {
      this.querySelector('.quick-view-content')?.classList.remove('quick-view-updating');
      this.backdrop.style.display = 'flex';
      document.body.classList.add('quick-view-modal-open');
      document.body.style.overflow = 'hidden';

      if (!state.productData) {
        this.container.innerHTML = `<p class="quick-view-loading">Loading</p>`;
      } else {
        if (!this.querySelector('quick-view-gallery')) {
          this.container.innerHTML = `
            <quick-view-gallery></quick-view-gallery>
            <div class="quick-view-info-column">
              <quick-view-product-info></quick-view-product-info>
              <quick-view-variant-selector></quick-view-variant-selector>
              <quick-view-add-button></quick-view-add-button>
              <quick-view-details-link></quick-view-details-link>
            </div>
          `;
        }

        this.querySelectorAll('.quick-view-container-root > *, .quick-view-info-column > *').forEach((node) => {
          if ('renderState' in node) node.renderState = state;
        });
      }
    } else {
      this.querySelector('.quick-view-content')?.classList.remove('quick-view-updating');
      this.backdrop.style.display = 'none';
      if (this.chartDrawer) this.chartDrawer.style.display = 'none';
      this.container.innerHTML = '';
      document.body.classList.remove('quick-view-modal-open');
      document.body.style.overflow = '';
    }
  }

  renderSizeChartContent(sizeChartHtml) {
    const target = this.querySelector('#quickViewSizeChartTarget');
    if (!target) return;

    const doc = new DOMParser().parseFromString(sizeChartHtml || '', 'text/html');
    const existingSizeChart = doc.querySelector('#size_chart');
    const normalizedContent = existingSizeChart ? doc.body.innerHTML : `<div id="size_chart">${sizeChartHtml || ''}</div>`;

    target.innerHTML = `
      <div class="size-chart">
        <div class="size-chart__inner rte">
          <h2>size guide</h2>
          ${normalizedContent}
        </div>
      </div>
    `;

    if (typeof window.convertToTabs === 'function') window.convertToTabs('size_chart');
    if (window.innerWidth <= 768 && typeof window.transposeTables === 'function') window.transposeTables();
  }
  openPdpSizeChart(sizeChartHtml) {
    if (this.dataset.pdp !== 'true') return false;
    if (!sizeChartHtml || !sizeChartHtml.trim()) return false;

    const nativeContainer = document.querySelector('#size-chart-content');
    const nativeLink = document.querySelector('.size-chart-link[href="#size-chart-content"]');
    if (!nativeContainer || !nativeLink) return false;

    const originalHtml = nativeContainer.innerHTML;
    const doc = new DOMParser().parseFromString(sizeChartHtml, 'text/html');
    const existingSizeChart = doc.querySelector('#size_chart');
    const normalizedContent = existingSizeChart ? doc.body.innerHTML : `<div id="size_chart">${sizeChartHtml}</div>`;

    nativeContainer.innerHTML = `
      <div class="size-chart">
        <div class="size-chart__inner rte">
          <h2>size guide</h2>
          ${normalizedContent}
        </div>
      </div>
    `;

    if (typeof window.convertToTabs === 'function') window.convertToTabs('size_chart');
    if (window.innerWidth <= 768 && typeof window.transposeTables === 'function') window.transposeTables();

    const restore = () => {
      nativeContainer.innerHTML = originalHtml;
      document.removeEventListener('cbox_closed', restore);
    };
    document.addEventListener('cbox_closed', restore);

    nativeLink.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    return true;
  }
}
customElements.define('quick-view-modal', QuickViewModal);

/**
 * ============================================================================
 * 4. Sub-Components Render Tree Elements
 * ============================================================================
 */
class QuickViewGallery extends HTMLElement {
  set renderState(state) {
    if (this.dataset.activeHandle === state.activeHandle) {
      this.updateWishlistButton(state);
      return;
    }
    this.dataset.activeHandle = state.activeHandle;

    const images = state.productData.images || [];
    this.totalSlides = images.length;
    this.currentActiveIndex = 0;

    const slides = images.length
      ? images.map((src, index) => `
          <div class="quick-view-gallery-slide" data-index="${index}">
            <img src="${src}" alt="${state.productData.title}" loading="${index === 0 ? 'eager' : 'lazy'}">
          </div>
        `).join('')
      : `<div class="quick-view-gallery-slide"><div class="quick-view-no-image">No Image Available</div></div>`;

    this.innerHTML = `
      <div class="quick-view-gallery-shell">
        <div class="quick-view-gallery-slider">
          ${slides}
        </div>
        <button
          aria-label="Add to Wishlist"
          data-with-epi="true"
          class="quick-view-wishlist unsub-swym-wishlist-collection swym-button swym-heart swym-add-to-wishlist-view-product product_${state.productData.id}"
          data-swaction="addToWishlist"
          data-product-id="${state.productData.id}"
          data-variant-id="${state.selectedVariant?.id || state.productData.representative_variant_id || ''}"
          data-product-url="${window.location.origin}/products/${state.activeHandle}">
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="unsub-swym-fill-icon unsub-swym-icon">
            <path d="M5 0.25C7.95556 0.25 9.39815 2.72917 9.75 3.96875C10.1019 2.72917 11.5444 0.25 14.5 0.25C17.4556 0.25 19.25 2.70703 19.25 5.03125C19.25 7.6875 18.1944 9.28125 15.0278 12.4688C11.8541 15.6633 10.1019 16.8958 9.75 17.25C9.39815 16.8958 7.64592 15.6633 4.47222 12.4688C1.30556 9.28125 0.25 7.6875 0.25 5.03125C0.25 2.70703 2.04444 0.25 5 0.25Z" fill="#111111" stroke="#111111" stroke-width="0.5" stroke-linejoin="round"/>
          </svg>
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="unsub-swym-bordered-icon unsub-swym-icon">
            <path d="M5 0.25C7.95556 0.25 9.39815 2.72917 9.75 3.96875C10.1019 2.72917 11.5444 0.25 14.5 0.25C17.4556 0.25 19.25 2.70703 19.25 5.03125C19.25 7.6875 18.1944 9.28125 15.0278 12.4688C11.8541 15.6633 10.1019 16.8958 9.75 17.25C9.39815 16.8958 7.64592 15.6633 4.47222 12.4688C1.30556 9.28125 0.25 7.6875 0.25 5.03125C0.25 2.70703 2.04444 0.25 5 0.25Z" fill="white" fill-opacity="0.5" stroke="#111111" stroke-opacity="0.5" stroke-width="0.5" stroke-linejoin="round"/>
          </svg>
        </button>
        ${images.length > 1 ? `
          <button class="quick-view-gallery-arrow quick-view-gallery-prev" type="button" aria-label="Previous">
            <svg width="8" height="14" viewBox="0 0 10 16" fill="none"><path d="M8 2L2 8L8 14" stroke="#111" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <button class="quick-view-gallery-arrow quick-view-gallery-next" type="button" aria-label="Next">
            <svg width="8" height="14" viewBox="0 0 10 16" fill="none"><path d="M2 2L8 8L2 14" stroke="#111" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="quick-view-gallery-nav"><div class="quick-view-gallery-progress"></div></div>
        ` : ''}
      </div>
    `;

    this.bindWishlistButton();
    this.updateWishlistButton(state);
    this.initializeSliderEngine();
  }

  updateWishlistButton(state) {
    const button = this.querySelector('.quick-view-wishlist');
    if (!button) return;
    button.dataset.productId = state.productData.id;
    button.dataset.variantId = state.selectedVariant?.id || state.productData.representative_variant_id || '';
    button.dataset.productUrl = `${window.location.origin}/products/${state.activeHandle}`;
    this.syncWishlistState(button);
  }

  bindWishlistButton() {
    const button = this.querySelector('.quick-view-wishlist');
    if (!button || button.dataset.bound === 'true') return;
    button.dataset.bound = 'true';
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggleWishlist(button);
    });
  }

  getSwymProduct(button) {
    return {
      epi: Number(button.dataset.variantId),
      empi: Number(button.dataset.productId),
      du: button.dataset.productUrl
    };
  }

  withSwym(callback) {
    if (window._swat) {
      callback(window._swat);
      return;
    }
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(callback);
  }

  syncWishlistState(button) {
    const product = this.getSwymProduct(button);
    button.classList.remove('swym-added');
    this.withSwym((swat) => {
      if (typeof swat.fetchWrtEventTypeET !== 'function') return;
      swat.fetchWrtEventTypeET((items) => {
        const isAdded = (items || []).some(item => Number(item.epi || item.vid) === product.epi || Number(item.empi || item.id) === product.empi);
        button.classList.toggle('swym-added', isAdded);
        button.dataset.swaction = isAdded ? 'removeFromWishlist' : 'addToWishlist';
      }, swat.EventTypes?.addToWishlist);
    });
  }

  toggleWishlist(button) {
    const product = this.getSwymProduct(button);
    const isAdded = button.classList.contains('swym-added');
    button.disabled = true;
    this.withSwym((swat) => {
      const done = () => {
        window.UnsubWishlistSync.sync(product, !isAdded);
        button.disabled = false;
      };
      const fail = () => { button.disabled = false; };
      const addToWishlist = swat.addToWishList || swat.addToWishlist;
      const removeFromWishlist = swat.removeFromWishList || swat.removeFromWishlist;
      if (isAdded && typeof removeFromWishlist === 'function') removeFromWishlist.call(swat, product, done, fail);
      else if (!isAdded && typeof addToWishlist === 'function') addToWishlist.call(swat, product, done, fail);
      else button.disabled = false;
    });
  }

  initializeSliderEngine() {
    const slider = this.querySelector('.quick-view-gallery-slider');
    const prev = this.querySelector('.quick-view-gallery-prev');
    const next = this.querySelector('.quick-view-gallery-next');
    const progress = this.querySelector('.quick-view-gallery-progress');
    if (!slider) return;

    const updateUIState = () => {
      const index = Math.round(slider.scrollLeft / slider.clientWidth);
      if (progress && this.totalSlides > 1) {
        progress.style.width = `${100 / this.totalSlides}%`;
        progress.style.transform = `translateX(${index * 100}%)`;
      }
      if (prev) prev.style.display = (index === 0) ? 'none' : 'flex';
      if (next) next.style.display = (index === this.totalSlides - 1) ? 'none' : 'flex';
    };

    updateUIState();
    slider.addEventListener('scroll', updateUIState, { passive: true });

    prev?.addEventListener('click', (event) => {
      event.preventDefault(); event.stopPropagation();
      const index = Math.round(slider.scrollLeft / slider.clientWidth);
      if (index > 0) {
        slider.scrollTo({ left: (index - 1) * slider.clientWidth, behavior: 'smooth' });
      }
    });

    next?.addEventListener('click', (event) => {
      event.preventDefault(); event.stopPropagation();
      const index = Math.round(slider.scrollLeft / slider.clientWidth);
      if (index < this.totalSlides - 1) {
        slider.scrollTo({ left: (index + 1) * slider.clientWidth, behavior: 'smooth' });
      }
    });
  }
}
customElements.define('quick-view-gallery', QuickViewGallery);

class QuickViewProductInfo extends HTMLElement {
  set renderState(state) {
    const price = state.selectedVariant ? `$${(state.selectedVariant.price / 100).toFixed(2)}` : '';
    const badgeHtml = state.productData.badge_html?.includes('unsub-custom-badge') ? state.productData.badge_html : '';
    const brandName = state.productData.vendor || '';

    this.innerHTML = `
      <div class="quick-view-info-wrapper">
        ${(badgeHtml || brandName) ? `
          <div class="quick-view-product-meta">
            ${badgeHtml ? `<div class="quick-view-badge">${badgeHtml}</div>` : ''}
            ${brandName ? `<div class="quick-view-brand${badgeHtml ? ' quick-view-brand--after-badge' : ''}">${brandName}</div>` : ''}
          </div>
        ` : ''}
        <h2 class="quick-view-heading">${state.productData.title}</h2>
        <p class="quick-view-price">${price}</p>
      </div>
    `;
  }
}
customElements.define('quick-view-product-info', QuickViewProductInfo);

class QuickViewVariantSelector extends HTMLElement {
  set renderState(state) {
    if (this.dataset.activeHandle !== state.activeHandle) {
      this.dataset.activeHandle = state.activeHandle;
      this.innerHTML = '';
    }

    if (this.innerHTML !== '') {
      const label = this.querySelector('.quick-view-color-text');
      if (label) label.innerText = state.productData?.color_name || 'Default';
      this.querySelectorAll('.quick-view-swatch-ring').forEach((ring) => {
        const button = ring.querySelector('button');
        ring.style.border = button.dataset.handle === state.activeHandle ? '1px solid #000' : '1px solid transparent';
      });
      state.productData.options.forEach((option, optionIndex) => {
        const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
        if (isColorOption) return;
        const activeValue = state.selectedOptions[optionIndex];
        this.querySelectorAll('.quick-view-size-button').forEach((button) => {
          const isUnavailable = !this.isOptionValueAvailable(state, optionIndex, button.dataset.value);
          button.classList.toggle('active-size', button.dataset.value === activeValue);
          button.classList.toggle('unavailable-size', isUnavailable);
          button.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
        });
      });
      return;
    }

    const activeColorName = state.productData.color_name || 'Default';
    let html = `
      <div class="quick-view-swatches-component-wrapper">
        <div class="quick-view-label"><span class="quick-view-swatch-heading-label-title">COLOR:</span> <span class="quick-view-color-text">${activeColorName}</span></div>
        <div class="quick-view-swatch-row">
    `;

    Object.keys(state.masterGroupData || {}).forEach((handle) => {
      const swatch = state.masterGroupData[handle];
      const isSelected = handle === state.activeHandle;
      const bgImage = swatch.swatch_file_url || swatch.swatch_prod_image_url;
      const sortImage = swatch.swatch_file_url || swatch.swatch_prod_image_url;
      html += `
        <div class="quick-view-swatch-ring" style="border: ${isSelected ? '1px solid #000' : '1px solid transparent'};">
          <button
            type="button"
            class="quick-view-swatch-button"
            data-handle="${swatch.handle}"
            data-representative-id="${swatch.representative_variant_id}"
            data-bgset="${sortImage || ''}"
            data-fallback-bgset="${bgImage || ''}"
            title="${swatch.color_name}"
            style="background-color: ${'#eee'}; background-image: url(${bgImage || ''}); opacity: ${swatch.is_unavailable ? '0.4' : '1'};">
          </button>
        </div>
      `;
    });
    html += `</div></div>`;

    state.productData.options.forEach((option, optionIndex) => {
      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
      if (isColorOption) return;
      const hasChartData = state.productData.size_chart_html && state.productData.size_chart_html.trim() !== '';
      html += `
        <div class="quick-view-size-selector-component-block">
          <div class="quick-view-size-header">
            <span class="quick-view-size-title">${option.name}</span>
            ${hasChartData ? `<button type="button" class="quick-view-size-chart-trigger">Size Guide</button>` : ''}
          </div>
          <div class="quick-view-size-row" data-index="${optionIndex}">
            ${option.values.map((value) => {
              const isSelected = state.selectedOptions[optionIndex] === value;
              const isUnavailable = !this.isOptionValueAvailable(state, optionIndex, value);
              return `<button type="button" class="quick-view-size-button${isSelected ? ' active-size' : ''}${isUnavailable ? ' unavailable-size' : ''}" data-value="${value}" aria-disabled="${isUnavailable ? 'true' : 'false'}">${value}</button>`;
            }).join('')}
          </div>
        </div>
      `;
    });

    this.innerHTML = html;
    this.querySelectorAll('.quick-view-swatch-button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        window.QuickViewStore.switchProductLocal(button.dataset.handle, button.dataset.representativeId);
      });
    });
    this.querySelectorAll('.quick-view-size-button').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const optionIndex = parseInt(button.parentElement.dataset.index, 10);
        const currentSelections = [...state.selectedOptions];
        currentSelections[optionIndex] = button.dataset.value;
        window.QuickViewStore.setSelectedOptions(currentSelections);
      });
    });
    this.querySelector('.quick-view-size-chart-trigger')?.addEventListener('click', (event) => {
      event.preventDefault();
      const modal = document.querySelector('quick-view-modal');
      const drawer = document.getElementById('quickViewSizeChartDrawer');
      if (modal?.openPdpSizeChart(state.productData.size_chart_html)) return;
      if (modal && drawer) {
        modal.renderSizeChartContent(state.productData.size_chart_html);
        drawer.style.display = 'flex';
      }
    });
    requestAnimationFrame(() => sortQuickViewSwatchesByLightness(this));
  }

  isOptionValueAvailable(state, optionIndex, value) {
    if (!state.productData?.variants) return false;
    const targetOptions = state.productData.options.map((option, index) => {
      const isColorOption = option.name.toLowerCase() === 'color' || option.name.toLowerCase() === 'colour';
      if (isColorOption) return state.masterGroupData?.[state.activeHandle]?.color_name || state.selectedOptions[index] || option.values[0];
      return index === optionIndex ? value : state.selectedOptions[index];
    });
    const variant = state.productData.variants.find(candidate =>
      candidate.options.every((optionValue, index) => optionValue === targetOptions[index])
    );
    if (!variant || !variant.available) return false;
    const maxQuantity = window.QuickViewStore.getMaxQuantityForState({ ...state, selectedVariant: variant });
    return maxQuantity === null || maxQuantity > 0;
  }
}
customElements.define('quick-view-variant-selector', QuickViewVariantSelector);

class QuickViewAddButton extends HTMLElement {
  set renderState(state) {
    const variantExists = !!state.selectedVariant;
    const maxQuantity = window.QuickViewStore.getMaxQuantityForState(state);
    const hasStock = maxQuantity === null || maxQuantity > 0;
    const isAvailable = variantExists && state.selectedVariant.available && hasStock;
    const text = !variantExists ? 'Unavailable Selection' : !isAvailable ? 'Out of Stock' : 'Add To Bag';

    this.innerHTML = `<button class="quick-view-add-submit" ${!isAvailable ? 'disabled' : ''}>${text}</button>`;
    this.querySelector('button')?.addEventListener('click', () => this.addToCart(state));
  }

  async addToCart(state) {
    const button = this.querySelector('button');
    const modalContent = this.closest('.quick-view-content');
    button.disabled = true;
    button.innerText = 'Adding...';
    modalContent?.classList.add('quick-view-updating');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: state.selectedVariant.id, quantity: 1 })
      });
      if (!response.ok) throw new Error('Unable to add product to cart.');
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, detail: { cart, scope: 'cart-summary' } }));
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
      window.QuickViewStore.closeModal();
      if (typeof window.openCustomDrawer === 'function') window.openCustomDrawer();
    } catch (error) {
      console.error('Quick view add to cart error:', error);
      modalContent?.classList.remove('quick-view-updating');
      button.disabled = false;
      button.innerText = 'Error Adding';
    }
  }
}
customElements.define('quick-view-add-button', QuickViewAddButton);

class QuickViewDetailsLink extends HTMLElement {
  set renderState(state) {
    this.innerHTML = `<a class="quick-view-details" href="/products/${state.activeHandle}">View Full Details</a>`;
  }
}
customElements.define('quick-view-details-link', QuickViewDetailsLink);
